import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CommonService } from './common.service';
import { AuthService } from './auth.service';
import { AlertNotification } from '../models';

/**
 * NotificationService — Phase 5 / C3 (ihub Notification Center).
 *
 * Wraps CommonService.getAlerts(phone) and holds the shared notification state that
 * both the header bell badge and the ih-notifications page observe.
 *
 * Read-state is CLIENT-SIDE only: customers authenticate by phone → InsuredCode and have no
 * numeric UserId, so the server-side notification_read_status table (keyed on int UserId) can't
 * store their reads. We persist the set of read alert ids in localStorage (survives WebView
 * restarts, unlike sessionStorage). Cross-device / reinstall read-sync is intentionally deferred.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly READ_KEY = 'ih_read_alerts';

  private notificationsSubject = new BehaviorSubject<AlertNotification[]>([]);
  notifications$ = this.notificationsSubject.asObservable();

  private unreadCountSubject = new BehaviorSubject<number>(0);
  unreadCount$ = this.unreadCountSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingSubject.asObservable();

  private errorSubject = new BehaviorSubject<boolean>(false);
  error$ = this.errorSubject.asObservable();

  constructor(
    private commonService: CommonService,
    private authService: AuthService
  ) {}

  /** (Re)load the caller's notifications from the backend and re-apply client read-state. */
  refresh(): void {
    const phone = this.authService.getUserPhone();
    if (!phone) {
      this.notificationsSubject.next([]);
      this.unreadCountSubject.next(0);
      return;
    }

    this.loadingSubject.next(true);
    this.errorSubject.next(false);

    this.commonService.getAlerts(phone).subscribe({
      next: (alerts) => {
        this.applyReadState(alerts || []);
        this.loadingSubject.next(false);
      },
      error: () => {
        this.errorSubject.next(true);
        this.loadingSubject.next(false);
      }
    });
  }

  /** Mark one notification read (client-side), then refresh derived state. */
  markRead(id: number): void {
    const readSet = this.getReadSet();
    if (!readSet.has(id)) {
      readSet.add(id);
      this.saveReadSet(readSet);
      this.applyReadState(this.notificationsSubject.value);
    }
  }

  /** Mark all currently-loaded notifications read (client-side). */
  markAllRead(): void {
    const readSet = this.getReadSet();
    let changed = false;
    for (const n of this.notificationsSubject.value) {
      if (!readSet.has(n.id)) { readSet.add(n.id); changed = true; }
    }
    if (changed) {
      this.saveReadSet(readSet);
      this.applyReadState(this.notificationsSubject.value);
    }
  }

  /**
   * Map a backend alert url to an ihub route. Backend urls are like
   * `/IHPayPremiumHistory/{policyNo}`, `/IHClaimDeadList/{policyNo}`, `/IHClaimAccidentList/{policyNo}`.
   * Anything unrecognised falls back to the policy detail (when we have a policy) or home.
   */
  mapUrlToRoute(url: string | null, policyNo: string | null): string {
    if (url) {
      const parts = url.split('/').filter(Boolean);
      const root = (parts[0] || '').toLowerCase();
      const arg = parts[1];
      if (arg) {
        switch (root) {
          // Deliberately NOT /ih-pay-premium-history: that page never calls the API and
          // renders hardcoded sample payments (RCP-001/RCP-002 plus a NEWYEAR50 voucher
          // that has never existed — the vouchers table is empty in every environment).
          // Over 10,000 live alerts on PROD point here, so this map was showing customers
          // fabricated payment records that were not theirs. Send them to the premium
          // schedule instead: real data, same subject. Point this back at the history page
          // once it is wired to GET /api/v1/ih/payments?policyNo= (built in phase 3b).
          case 'ihpaypremiumhistory': return `/ih-premium-schedul/${arg}`;
          case 'ihclaimdeadlist': return `/ih-claim-dead-list/${arg}`;
          case 'ihclaimaccidentlist': return `/ih-claim-accident-list/${arg}`;
        }
      }
    }
    return policyNo ? `/ih-policy-detail/${policyNo}` : '/ih-home';
  }

  // ── internal ──────────────────────────────────────────────────────────────

  private applyReadState(alerts: AlertNotification[]): void {
    const readSet = this.getReadSet();
    const withRead = alerts.map(a => ({ ...a, isRead: readSet.has(a.id) }));
    this.notificationsSubject.next(withRead);
    this.unreadCountSubject.next(withRead.filter(a => !a.isRead).length);
  }

  private getReadSet(): Set<number> {
    try {
      const raw = localStorage.getItem(this.READ_KEY);
      return new Set<number>(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set<number>();
    }
  }

  private saveReadSet(readSet: Set<number>): void {
    try {
      localStorage.setItem(this.READ_KEY, JSON.stringify([...readSet]));
    } catch {
      // localStorage unavailable (private mode / quota) — read-state is best-effort.
    }
  }
}
